import { useState, useEffect } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { Logo } from "@/components/Logo";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { Eye, EyeSlash, CheckCircle } from "@phosphor-icons/react";

export default function ResetPassword() {
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const nav = useNavigate();
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    if (!token) toast.error("Link inválido — abra o e-mail novamente");
  }, [token]);

  const submit = async (e) => {
    e.preventDefault();
    if (password !== confirm) { toast.error("As senhas não conferem"); return; }
    if (password.length < 6) { toast.error("Mínimo 6 caracteres"); return; }
    setBusy(true);
    try {
      await api.post("/auth/reset-password", { token, new_password: password });
      setDone(true);
      toast.success("Senha alterada!");
      setTimeout(() => nav("/login"), 2500);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erro ao redefinir senha");
    } finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-6 bg-slate-50">
      <Card className="w-full max-w-md p-6 md:p-8 border-2">
        <div className="flex justify-center mb-4"><Logo size={56} /></div>
        <h2 className="text-2xl font-extrabold tracking-tight text-center" style={{ fontFamily: "Manrope" }}>Redefinir senha</h2>
        <p className="text-sm text-muted-foreground mt-1 text-center">Crie uma nova senha pra acessar sua conta</p>

        {done ? (
          <div className="mt-6 text-center space-y-3" data-testid="reset-success">
            <CheckCircle size={64} weight="fill" className="mx-auto text-green-600" />
            <p className="font-semibold text-slate-800">Senha alterada com sucesso!</p>
            <p className="text-sm text-slate-500">Redirecionando pro login...</p>
          </div>
        ) : (
          <form onSubmit={submit} className="space-y-4 mt-6">
            <div>
              <Label>Nova senha</Label>
              <div className="relative mt-1.5">
                <Input type={showPassword ? "text" : "password"} value={password} onChange={(e) => setPassword(e.target.value)} required placeholder="Mínimo 6 caracteres" data-testid="reset-new-password" className="pr-10" />
                <button type="button" onClick={() => setShowPassword(!showPassword)} className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-500 hover:text-slate-800 p-1" aria-label="Alternar visibilidade">
                  {showPassword ? <EyeSlash size={18} /> : <Eye size={18} />}
                </button>
              </div>
            </div>
            <div>
              <Label>Confirme a nova senha</Label>
              <Input type={showPassword ? "text" : "password"} value={confirm} onChange={(e) => setConfirm(e.target.value)} required data-testid="reset-confirm-password" className="mt-1.5" />
            </div>
            <Button type="submit" disabled={busy || !token} data-testid="btn-reset-submit" className="w-full bg-blue-600 hover:bg-blue-700 text-white h-11">
              {busy ? "Alterando..." : "Alterar senha"}
            </Button>
            <p className="text-sm text-center">
              <Link to="/login" className="text-blue-700 hover:underline">← Voltar ao login</Link>
            </p>
          </form>
        )}
      </Card>
    </div>
  );
}
